import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(tz);

import { useFetch } from 'src/lib/FetchContext';
import { usePrefetchSuspense } from 'src/lib/usePrefetch';
import { interestLevels } from 'src/lib/interestLevels';
import { itemClassMap } from 'src/lib/itemClassMap';
import { sanitizeWithDashes } from 'src/lib/catalog';

import {
  Button,
  Banner,
  cn,
  Grid,
  GridItem,
  CallToAction,
  ShareBar,
  SocialSharingProvider,
  TextField,
  Typography,
  Spinner,
  convertGhostHTMLToReactComponents,
  Tabs,
  TabsItem,
  LotShowcase,
  Grid2,
} from '@riac/design-system';
import { IconSearch } from '@riac/design-system';
import { IconArrowLeft } from '@riac/design-system';
import { IconArrowRight } from '@riac/design-system';

import { DetailPageActionArea } from 'src/pages/DetailPage/components/DetailPageActionArea';

import { DetailPageContext } from 'src/pages/DetailPage/DetailPageContext';
import { CatalogHistoryContext } from 'src/lib/CatalogHistoryContext';
import { UserItemContextProvider } from 'src/lib/UserItemContext';
import { WishlistActionContext } from 'src/lib/WishlistActionContext';
import { LoginContext } from 'src/lib/LoginContext';
import { ParsedHeadlineMess, parseLotHeadlineMess } from 'src/lib/auctionItemUtils';
import { BiddingContextProvider } from 'src/lib/BiddingContext';
import { SocketContext } from 'src/lib/SocketContext';
import { cookError, loglog } from 'src/lib/loglog';
import { GenericPageHeader } from 'src/components/GenericPageHeader';
import { DetailPageRelatedItemsSection } from './components/DetailPageRelatedItemsSection';
import { DetailPageQuestionSection } from './components/DetailPageQuestionSection';
import { AuctionPreview } from 'src/components/AuctionPreview/AuctionPreview';

let timer = null;

const detailPageDetailsContainer = 'w-full text-left border-surface-darker border-1 flex flex-row flex-wrap';
const detailPageDetailsColumn = 'flex-1';

const DetailPageDetailsRow = ({ heading, content, className }: {
  heading: JSX.Element | string,
  content: JSX.Element | string | null | undefined,
  className?: string,
}) => {
  if(!content) return null;
  return (
    <div className={cn('flex flex-col', className)}>
      <Typography color='text-lighter' weight='bold'>{heading}</Typography>
      <Typography color='text' weight='bold' size='sm'>{content}</Typography>
    </div>
  );
};
export const DetailPage = (): JSX.Element => {
  const { fetch } = useFetch();
  const history = useHistory();
  const params = useParams<{ auctionid: string, lotnumber: string, slug: string }>();
  const { latestCatalogPage } = useContext(CatalogHistoryContext);

  const auctionId = parseInt(params.auctionid);
  const lotNumber = parseInt(params.lotnumber);
  const [initLoad, setInitLoad] = useState<boolean>(true);
  const [lotInput, setLotInput] = useState<string>('');

  const wishlistActions = useContext(WishlistActionContext);
  const { isLoggedIn } = useContext(LoginContext);
  const [loading, setLoading] = useState(false);
  const { socket, setOnOnTimePage } = useContext(SocketContext);

  useEffect(() => {
    if(isLoggedIn) {
      wishlistActions.setAuction(auctionId);
    }
  }, [auctionId]);

  const itemInfoDefaults = {
    Photos: [],
    timestamp: null,
  };

  const refetchData = async (auctionId, lotNumber) => {
    const data = await (await fetch(`/api/detail/${auctionId}/${lotNumber}`)).json();
    data.timestamp = Date.now();
    setItemInfo(Object.assign({}, itemInfoDefaults, data));
  };

  const onLotChange = async () => {
    try {
      if(initLoad) {
        setInitLoad(false);
      } else {
        await fetch(`/api/detail/view/${auctionId}/${lotNumber}`);
      }
      setLoading(true);
      refetchData(auctionId, lotNumber).then(() => setLoading(false));
      setLotInput('');
    } catch (err) {
      loglog.error('Lot Change Update', { err: cookError(err) });
    }
  };

  const prefetchedItemInfo = usePrefetchSuspense('detail-page', async () => {
    await fetch(`/api/detail/view/${auctionId}/${lotNumber}`);
    const detailResponse = await fetch(`/api/detail/${auctionId}/${lotNumber}`);
    const detailResults = await detailResponse.json();
    detailResults.timestamp = Date.now();
    return detailResults;
  }).read();
  const [itemInfo, setItemInfo] = useState(Object.assign({}, itemInfoDefaults, prefetchedItemInfo));
  let primaryItem = itemInfo?.Items[0] || null;

  useEffect(() => {
    primaryItem = itemInfo?.Items[0] || null;
    if(itemInfo?.Photos[0] && primaryItem) {
      primaryItem.Photo = itemInfo.Photos[0];
    }
  }, [itemInfo.timestamp, auctionId, lotNumber]);

  useEffect(() => {
    onLotChange();
  }, [auctionId, lotNumber]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, []);

  // parse headlines
  const parsedHeadlines: ParsedHeadlineMess = primaryItem && itemInfo.Items.map((item) => parseLotHeadlineMess(item.Headline, item.LotExt));
  // STUB: If this resolves to null the whole page breaks
  // not sure if that's by design or not
  const primaryParsedHeadline = primaryItem && parsedHeadlines?.[0]?.lotHeadline;

  useEffect(() => {
    if(socket && itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      socket.emit('join-room', `timed-auction-${auctionId}`);
      setOnOnTimePage(true);
    } else {
      setOnOnTimePage(false);
    }
    return () => {
      if(socket && itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        socket.emit('leave-room', `timed-auction-${auctionId}`);
        setOnOnTimePage(false);
      }
    };
  }, [socket?.id, itemInfo?.AuctionTypeID]);

  const updateTimedItemInfo = (item, currentBid, endDate, highBidder, highBidAmount) => {
    const newItem = { ...item };
    newItem.CurrentBid = currentBid;
    newItem.EndDate = endDate;

    // If we are the high bidder and the current bid is over our high bid, we are no longer the high bid
    if(newItem.HighBidder && currentBid > newItem.HighBidAmount) {
      newItem.HighBidder = false;
      newItem.PreviousBid = true;
      newItem.HighBidAmount = null;
    }
    // if we are now the high bidder
    if(typeof highBidder !== 'undefined' && typeof highBidAmount !== 'undefined') {
      newItem.HighBidAmount = highBidAmount;
      newItem.HighBidder = highBidder;
      if(!highBidder) {
        newItem.PreviousBid = 1;
        newItem.HighBidAmoun = null;
      }
    }
    return newItem;
  };

  useEffect(() => {
    const lotUpdateHandler = (
      auctionItemID: number | string,
      currentBid: number,
      endDate: string,
      highBidder?: boolean,
      highBidAmount?: number | null,
    ): void => {
      if(String(itemInfo?.Items[0]?.AuctionItemID) !== String(auctionItemID)) {
        return;
      }

      addTimer(auctionId, lotNumber, endDate);
      setItemInfo((oldItem) => ({
        ...updateTimedItemInfo(oldItem, currentBid, endDate, highBidder, highBidAmount),
        Items: oldItem.Items.map((oi, i) => {
          if(i === 0) {
            return updateTimedItemInfo(oi, currentBid, endDate, highBidder, highBidAmount);
          }
          return oi;
        }),
      }));
    };
    if(socket && itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      socket.on('lot update', lotUpdateHandler);
    }
    return () => {
      if(socket && itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        socket.off('lot update', lotUpdateHandler);
      }
    };
  }, [itemInfo.timestamp, auctionId, lotNumber]);

  const addTimer = (aId, lotNumber, endDate) => {
    if(timer) {
      clearTimeout(timer);
      timer = null;
    }
    const now = dayjs();
    const end = dayjs.tz(endDate, 'America/Chicago'); // DayJS Note: Interpret endDate as America/Chicago.
    const diff = end.diff(now);
    if(diff > 0 && diff < 86400000) {
      timer = setTimeout(async () => {
        await updateTimedInfo(aId, lotNumber);
      }, diff);
    }
  };

  const clearTimer = () => {
    if(timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const updateTimedInfo = async (aId, lot) => {
    const timedInfo = await (await fetch('/api/timed-auction/refresh-lot-data', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        {
          auctionId: aId,
          lotNumbers: lot,
        }
      ),
    })).json();
    if(timedInfo && timedInfo.success && timedInfo.data[lot]) {
      addTimer(auctionId, lotNumber, timedInfo.data[lot].endDate);
      setItemInfo((oldItem) => ({
        ...updateTimedItemInfo(oldItem, timedInfo.data[lot].currentBid, timedInfo.data[lot].endDate, timedInfo.data[lot].highBidder, timedInfo.data[lot].highBidAmount),
        Items: oldItem.Items.map((oi, i) => {
          if(i === 0) {
            return updateTimedItemInfo(oi, timedInfo.data[lot].currentBid, timedInfo.data[lot].endDate, timedInfo.data[lot].highBidder, timedInfo.data[lot].highBidAmount);
          }
          return oi;
        }),
      }));
    }
  };

  useEffect(() => {
    const visChangeHandler = async (event) => {
      if(!document.hidden) {
        await updateTimedInfo(auctionId, lotNumber);
      }
    };

    const reconHandler = async () => {
      await updateTimedInfo(auctionId, lotNumber);
    };
    if(itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      addTimer(auctionId, lotNumber, itemInfo.EndDate);
      window.addEventListener('focus', visChangeHandler);
      socket.on('reconnect', reconHandler);
      socket.on('connect', reconHandler);
    }

    return () => {
      if(itemInfo?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        clearTimer();
        window.removeEventListener('focus', visChangeHandler);
        socket.off('reconnect', reconHandler);
        socket.off('connect', reconHandler);
      }
    };
  }, [itemInfo.timestamp, auctionId, lotNumber]);


  if(!primaryItem) {
    return (<>
      <GenericPageHeader title=''/>
      <Grid2>
        <Banner color='error'>
          <h1>We're sorry - we can't find the lot you're looking for.</h1>
          <span className='font-medium'>
            We might have removed the page when we redesigned our website, the link you clicked
            might be old and does not work anymore, or you might have accidentally typed the wrong
            URL in the address bar. To find other items in this auction, you can visit our{' '}
            <Button variant='link' className='p-0' to={latestCatalogPage || `/catalog/${auctionId}`}>Catalog Page</Button>
          </span>
        </Banner>
      </Grid2>
      <Grid2>
        <CallToAction
          title={'Didn\'t find what you were looking for?'}
          text={'Rock Island Auction Company is the global leader in firearms auctions, '
            + 'currently holding multiple records for our top-selling lots. View our latest '
            + 'catalog to find what you might be searching for.'}
          button={<Button to='/catalog'>View Catalog</Button>}
        />
      </Grid2>
    </>);
  }

  const detailSlug = sanitizeWithDashes(primaryItem.WebTitle).toLowerCase();

  // Redirect to correct slug if it doesn't exist or is wrong.
  if(!params.slug || params.slug !== detailSlug) {
    return <Redirect
      to={`/detail/${params.auctionid}/${params.lotnumber}/${detailSlug}`}
    />;
  }

  const videos = itemInfo?.Items.reduce((prev, cur) => {
    if(!cur.VideoLink) {
      return prev;
    }
    return [
      ...prev,
      ...cur.VideoLink.split(','),
    ];
  }, []);

  const AuctionStatusID = itemInfo?.AuctionStatusID;
  const SessionStatus = (itemInfo?.Items[0]?.AuctionSessionStatus || 0);
  const aPriceRealized = !!itemInfo?.PricesRealized;
  const AuctionTypeID = itemInfo?.AuctionTypeID;

  let allowBid = false;
  const now = dayjs();
  const completedLot = itemInfo && (
    dayjs.tz(itemInfo.StartDate, 'America/Chicago').isBefore(now) && dayjs.tz(itemInfo.EndDate, 'America/Chicago').add(24, 'hour').isAfter(now)
  ) && (
    itemInfo.activeLot > itemInfo.Items[0].LotNumber
  );
  if(AuctionStatusID === 2 && (((SessionStatus === 2 || SessionStatus === 3) && !aPriceRealized && !completedLot) || AuctionTypeID === TIMED_AUCTION_TYPE_ID)) {
    allowBid = true;
  }

  const tabs = ['Description'];
  // Only show questions section for currently active auctions.
  if(dayjs.tz(itemInfo.EndDate, 'America/Chicago').add(24, 'hour').isAfter(now)) {
    tabs.push('Questions');
  }

  return (

    <SocialSharingProvider>
      <DetailPageContext.Provider value={{}}>
        <BiddingContextProvider
          auctionId={auctionId}
          auctionTypeId={itemInfo?.AuctionTypeID}
        >
          <GenericPageHeader
            title={<div className='flex flex-col gap-1'>
              <Typography weight='semibold' expressive size='sm' display>Lot {lotNumber}:</Typography>
              <Typography weight='semibold' expressive display>{itemInfo?.AuctionTypeID > 1 ? primaryParsedHeadline : primaryItem.WebTitle}</Typography>
            </div>}
            breadcrumbs={[
              { slug: '/gun-auctions', text: 'Auctions' },
              { slug: `/catalog/${auctionId}`, text: 'Catalog' },
              { slug: `/detail/${auctionId}/${lotNumber}`, text: `Lot ${lotNumber}` },
            ]}
            description={<>
              <div className='flex flex-row items-center gap-1'>
                {itemInfo?.AuctionTypeID !== TIMED_AUCTION_TYPE_ID
                  && <div className={'flex-grow flex flex-row justify-right gap-1 dark'}>
                    <Typography size='sm' className='text-text-contrast-light'>{dayjs(primaryItem?.Date).format('MMMM Do, YYYY')}</Typography>
                    <Typography size='sm' className='text-text-contrast-light'>|</Typography>
                    <Typography size='sm' className='text-text-contrast-light'>{itemInfo?.LocCity}, {itemInfo?.LocState}</Typography>
                  </div>
                  || <div />
                }
              </div>
            </>}
            breadcrumbsAddon={(<div className={'flex flex-row items-center gap-3 sm:min-w-48'}>
              <TextField
                type='text'
                aria-label='Go To Lot'
                placeholder='Lot #'
                value={lotInput}
                onChange={(val) => setLotInput(val)}
                onKeyDown={(e) => e.key === 'Enter' && lotInput.length > 0 && history.push(`/detail/${params.auctionid}/${lotInput}`)}
                className='w-full md:w-[235px] light'
                after={
                  <Button
                    onPress={() => {
                      lotInput.length > 0 && history.push(`/detail/${params.auctionid}/${lotInput}`);
                      lotInput.length > 0 && setLoading(true);
                    }}
                    size='sm'
                    className='px-1.5 bg-neutral-400'
                  >
                    <IconSearch className='size-6' />
                  </Button>
                }
              />
              <div className='flex flex-row gap-1 text-white items-center'>
                <Button
                  variant='text'
                  className='p-0'
                  isDisabled={!itemInfo?.PrevLot}
                  to={`/detail/${auctionId}/${itemInfo.PrevLot}`}
                  onPress={() => {
                    setLoading(true);
                    history.push(`/detail/${auctionId}/${itemInfo.PrevLot}`);
                  }}
                >
                  <IconArrowLeft className='size-7' />
                </Button>
                <Button
                  variant='text'
                  className='p-0'
                  isDisabled={!itemInfo?.NextLot}
                  to={`/detail/${auctionId}/${itemInfo.NextLot}`}
                  onPress={() => {
                    setLoading(true);
                    history.push(`/detail/${auctionId}/${itemInfo.NextLot}`);
                  }}
                >
                  <IconArrowRight className='size-7' />
                </Button>
              </div>
            </div>)}
          >
          </GenericPageHeader>
          <UserItemContextProvider auctionId={auctionId}>
            <Grid2 className='py-20'>
              <div className='flex flex-col md:flex-row gap-5 lg:gap-10'>
                <LotShowcase
                  videos={videos}
                  photos={itemInfo?.Photos}
                  CDN_PATH={CDN_PATH}
                  auctionId={auctionId}
                />
                <div className='flex-1 flex flex-col gap-4 md:max-w-[373px]'>
                  <DetailPageActionArea
                    auctionId={auctionId}
                    itemInfo={itemInfo}
                    allowBid={allowBid}
                    primaryItem={primaryItem}
                  />
                  <div className='flex flex-row gap-3 items-center justify-center'>
                    <Typography color='text-light' className='text-base leading-6'>Share</Typography>
                    <div className='flex flex-row gap-2 text-text'>
                      <ShareBar
                        title={primaryItem.Headline}
                        text='Share this item:'
                        image={`${window.CDN_PATH}${auctionId}/${itemInfo.Photos[0]}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Grid2>
            <Grid2 className='py-20 pt-0 overflow-hidden'>
              <Typography expressive heading tag='h4' color='text' weight='bold' className='pb-5'>
                {(() => {
                  const parsed = parsedHeadlines[0];

                  const ret = parsed.lotHeadline;
                  return ret;
                })()}
              </Typography>
              <Tabs
                tabs={tabs}
                defaultTab='Description'
              >
                <div className='flex flex-col gap-10'>
                  <TabsItem tab='Description'>
                    {itemInfo?.Items.map((item, itemIndex) => (

                      <div key={itemIndex} className='flex flex-col gap-2'>
                        <div className='flex flex-col gap-0 pb-5'>
                          <Typography expressive heading tag='h4' color='text' weight='bold'>
                            {(() => {
                              const parsed = parsedHeadlines[itemIndex];

                              const ret = parsed.extension
                                ? `${item.LotExt}) ${parsed.extensionHeadline}`
                                : '';
                              return ret;
                            })()}
                          </Typography>
                          {item.HTMLDescription
                            && <Typography color='text'><div className='prose'
                              dangerouslySetInnerHTML={{
                                __html: `${item.HTMLDescription.replace(/color: black;/g, '')}`,
                              }}
                            /></Typography>
                            || <Typography color='text'>
                              <p>{item.DetailDesc || item.ModelDescription}</p>
                            </Typography>
                          }
                        </div>
                        {item.ConditionDesc
                          && <div className='flex flex-col gap-0 pb-5'>
                            <Typography heading expressive color='text' weight='bold'>Condition</Typography>
                            <Typography>{item.ConditionDesc}</Typography>
                          </div>
                        }
                        <div className='py-10 light relative flex flex-col gap-2'>
                          <div className='absolute -left-[100vw] -right-[100vw] bottom-0 top-0 -z-10 bg-[#EBEAE9]'/>
                          <Typography expressive heading color='text'>
                            Details
                          </Typography>
                          <div className='grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-[25px]'>
                            <DetailPageDetailsRow heading='Manufacturer' content={item.ManufacturerName} className='col-start-1 col-end-1'/>
                            <DetailPageDetailsRow heading='Model' content={item.ModelDescription} />
                            <DetailPageDetailsRow heading='Type' content={item.GunType || item.OtherType} />
                            <DetailPageDetailsRow heading='Serial number' content={item.SerialNumber
                              && (<div className='overflow-hidden'> {/* hide the reverse margin. For some reason this image has some left margin */}
                                <img src={item.SerialNumber} className='h-[20px] -ml-1' />
                              </div> )
                            } />
                            <DetailPageDetailsRow heading='Class' content={itemClassMap[item.A_MCode]} className='col-start-1 col-end-1'/>
                            <DetailPageDetailsRow heading='Caliber / Gauge' content={item.Guage} />
                            <DetailPageDetailsRow heading='Barrel' content={item.BBL} />
                            <DetailPageDetailsRow heading='Finish' content={item.Finish} />
                            <DetailPageDetailsRow heading='Stock' content={item.Stock} />
                            <DetailPageDetailsRow heading='Grip' content={item.Grips} />
                            <DetailPageDetailsRow className={cn('col-span-2', item.Grips && item.Stock ? 'md:col-span-2' : 'md:col-span-3')} heading='Bore condition' content={item.BoreCondition} />
                            <div className='flex flex-col col-span-2 md:col-span-4'>
                              <hr className='w-3/4 border-border-active my-0 mx-auto' />
                            </div>
                            <DetailPageDetailsRow heading='Item Location' content={`${itemInfo?.LocCity}, ${itemInfo?.LocState}`} className='col-start-1 col-end-1'/>
                            <DetailPageDetailsRow heading='Views' content={item.ItemViews} />
                            <DetailPageDetailsRow heading='Interest' content={item.ItemInterest > 0
                              ? (interestLevels[item.ItemInterest > 3 ? 3 : item.ItemInterest].description) : undefined
                            } />
                            <DetailPageDetailsRow heading='Catalog page' content={item.CatPage} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </TabsItem>
                  <TabsItem tab='Questions'>
                    <div className='flex flex-col items-stretch xl:flex-row gap-10 xl:items-start'>
                      <div className='flex flex-col gap-5 flex-1'>
                        <Typography size='sm' heading weight='bold'>Customer Product Questions</Typography>
                        <div className='flex-1 flex flex-col bg-surface-dark gap-5 p-5 '>
                          {itemInfo?.CustomerQuestions?.length <= 0
                            && <div className='border-1 border-border-dark bg-surface p-3'>
                              <Typography color='text-light'>There are currently no customer product questions on this lot.</Typography>
                            </div>
                          }
                          {itemInfo?.CustomerQuestions?.map((question, index) => (
                            <div key={index} className='flex flex-col gap-4 bg-surface border-1 border-border-dark p-3'>
                              <div>
                                <Typography color='text-light' weight='semibold'>
                                  Question
                                </Typography>
                                {' '}
                                <Typography>
                                  {question.Question}
                                </Typography>
                              </div>
                              <div>
                                {question.Answer && <Typography color='text-light' weight='semibold'>Response</Typography>}
                                {question.Answer && <div className='prose max-w-full w-full text-text p-0'>
                                  {convertGhostHTMLToReactComponents(question.Answer)}
                                </div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className='flex-1'>
                        <DetailPageQuestionSection auctionItemId={primaryItem?.AuctionItemID}/>
                      </div>
                    </div>
                  </TabsItem>
                </div>
              </Tabs>
            </Grid2>
            <DetailPageRelatedItemsSection itemInfo={itemInfo} />
          </UserItemContextProvider>
        </BiddingContextProvider>
      </DetailPageContext.Provider>
    </SocialSharingProvider>
  );
};


export default DetailPage;
